import React from 'react';
import cn from 'classnames';

import { DEFAULT_STYLES, SEARCH_DEBOUNCE_MS } from '../../constants';
import { CloseIcon } from '../../icons/CloseIcon';
import { SearchIcon } from '../../icons/SearchIcon';
import { ComboBoxSearchInputProps } from '../../types/comboBoxComponentTypes';

export const ComboBoxSearchInput: React.FC<ComboBoxSearchInputProps> = ({
  mobileInputRef,
  inputValue,
  placeholder,
  isDisabled,
  isOpen,
  isMobile,
  hasError,
  isInputFocused,
  showClearButton,
  isFiltering = false,
  isClosingRef,
  isOpeningRef,
  debounceRef,
  isTypingRef,
  onInputChange,
  onSearch,
  onClear,
  setInputValue,
  setIsInputFocused
}) => {

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isClosingRef.current) return;

    const newValue = e.target.value;
    isTypingRef.current = true;
    setInputValue(newValue);
    onInputChange?.(newValue);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      if (!isClosingRef.current) {
        onSearch?.(newValue);
      }
      isTypingRef.current = false;
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsInputFocused(true);
    if (isClosingRef.current) {
      e.target.blur();
      return;
    }
    if (isMobile && isOpen) {
      isOpeningRef.current = true;
      setTimeout(() => {
        isOpeningRef.current = false;
      }, 200);
    }
  };

  const handleBlur = () => {
    if (isOpeningRef.current && isOpen) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (mobileInputRef.current && isOpen) {
            mobileInputRef.current.focus();
            requestAnimationFrame(() => {
              if (
                mobileInputRef.current &&
                isOpen &&
                document.activeElement !== mobileInputRef.current
              ) {
                mobileInputRef.current.focus();
              }
            });
          }
        }, 50);
      });
      return;
    }
    setTimeout(() => {
      if (!isOpeningRef.current && !isOpen) {
        setIsInputFocused(false);
      }
    }, 200);
  };

  const renderFilteringSpinner = () => (
    <div
      className="flex-shrink-0 mb-auto flex mt-auto text-cement"
      role="status"
      aria-label="Searching"
    >
      <svg
        className="w-4 h-4 animate-spin"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  );

  const renderClearButton = () => {
    if (!showClearButton || isFiltering) return null;

    return (
      <button
        type="button"
        onClick={onClear}
        className="flex-shrink-0 mb-auto flex mt-auto text-cement hover:text-slateText transition-colors"
        tabIndex={-1}
        aria-label="Clear selection"
      >
        <CloseIcon className="w-4 h-4 mb-auto flex mt-auto" />
      </button>
    );
  };

  return (
    <div className="px-4 py-1 flex-shrink-0 box-border">
      <div
        className={cn(
          DEFAULT_STYLES.field__inputContainer,
          hasError
            ? 'border-errorRed'
            : isOpen || isInputFocused
              ? 'border-lightIndigo600 ring-2 ring-lightIndigo600 ring-opacity-50 input-container-focused'
              : DEFAULT_STYLES.field__inputContainerDefault,
          isDisabled && DEFAULT_STYLES.field__inputContainerDisabled
        )}
      >
        <div
          className="flex items-center justify-center flex-shrink-0 text-cement pointer-events-none"
          aria-hidden="true"
        >
          <SearchIcon width={16} height={16} />
        </div>

        <input
          ref={mobileInputRef}
          type="text"
          value={inputValue}
          placeholder={placeholder}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={isDisabled}
          autoFocus={isOpen && !isDisabled}
          className={cn(
            DEFAULT_STYLES.field__input,
            DEFAULT_STYLES.field__inputText,
            DEFAULT_STYLES.field__inputPlaceholder,
            isDisabled && DEFAULT_STYLES.field__inputDisabled
          )}
        />

        {isFiltering ? renderFilteringSpinner() : renderClearButton()}
      </div>
    </div>
  );
};

ComboBoxSearchInput.displayName = 'ComboBoxSearchInput';
